import { useState, useCallback } from 'react';
import { transactionsAPI } from '../api/client';

/**
 * useTransactions — hook for managing investment transactions.
 *
 * Provides: transaction list, loading state, CRUD operations,
 * and filtering by goal.
 */
export function useTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
  });

  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await transactionsAPI.list(params);
      const data = res.data;

      if (data.results !== undefined) {
        // Paginated response
        setTransactions(data.results);
        setPagination({
          count: data.count,
          next: data.next,
          previous: data.previous,
          page: params.page || 1,
        });
      } else {
        setTransactions(data);
      }
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to fetch transactions.';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchByGoal = useCallback(async (goalId, params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await transactionsAPI.getByGoal(goalId, params);
      const data = res.data.results || res.data;
      setTransactions(data);
      return data;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to fetch transactions for goal.';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createTransaction = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const res = await transactionsAPI.create(data);
      setTransactions((prev) => [res.data, ...prev]);
      return res.data;
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        Object.values(err.response?.data || {}).flat().join(' ') ||
        'Failed to create transaction.';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTransaction = useCallback(async (id, data, partial = true) => {
    setLoading(true);
    setError(null);
    try {
      const res = partial
        ? await transactionsAPI.partialUpdate(id, data)
        : await transactionsAPI.update(id, data);
      setTransactions((prev) => prev.map((t) => (t.id === id ? res.data : t)));
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update transaction.';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTransaction = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await transactionsAPI.delete(id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete transaction.';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    transactions,
    loading,
    error,
    pagination,
    fetchTransactions,
    fetchByGoal,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    clearError,
  };
}

export default useTransactions;
